"use client";

import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleAlert as AlertCircle, Loader, Mail, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "@/components/common/Link";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
const serviceOptions = ["Appliance Repair", "HVAC Services", "Plumbing Services", "Electrical Services", "Other"];
const readyItems = ["Property type (house, condo, office)", "Appliance model or system details", "Photos of the issue, if available", "Your preferred service window"];
function BookingForm() {
  const [formDef, setFormDef] = useState<any>(null);
  const [fetchTime, setFetchTime] = useState<number>(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hpValue, setHpValue] = useState("");
  const [formData, setFormData] = useState({
    full_name: "",
    phone: "",
    email: "",
    service_type: "",
    service_address: "",
    message: "",
    preferred_date: ""
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, {
    message: string;
  }>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState("Request received. A pro will contact you shortly.");
  useEffect(() => {
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      return;
    }
    client.pluginRequest("wvc-forms/v1/forms/homeowner_service_request").then((data: any) => {
      setFormDef(data);
      setFetchTime(Date.now());
    }).catch((err: any) => {
      if (err?.status === 404) {
        client.pluginRequest("wvc-forms/v1/forms").then((list: any) => {
          const items = list?.items || [];
          const matched = items.find((f: any) => f.key === "homeowner_service_request") || (items.length === 1 ? items[0] : null) || items.find((f: any) => f.name && /homeowner|service|request|booking/i.test(f.name));
          if (matched) {
            return client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`).then((res: any) => {
              setFormDef(res);
              setFetchTime(Date.now());
            });
          } else {
            setLoadError("missing");
          }
        }).catch(() => setLoadError("error"));
      } else {
        setLoadError("error");
      }
    });
  }, []);
  const getField = (key: string) => formDef?.fields?.find((f: any) => f.key === key);
  const getErrorMessage = (key: string, fieldId?: string) => {
    if (errors[key]) return errors[key];
    if (fieldId && serverErrors[fieldId]?.message) return serverErrors[fieldId].message;
    return null;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setServerErrors({});
    const newErrors: Record<string, string> = {};
    if (!formData.full_name.trim()) {
      newErrors.full_name = "Please enter your full name.";
    } else if (formData.full_name.trim().length < 2) {
      newErrors.full_name = "Min 2 characters";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Please enter your phone number.";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Please enter a valid email address.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = "Invalid email address";
    }
    if (!formData.service_type) {
      newErrors.service_type = "Please select a service type.";
    }
    if (!formData.service_address.trim()) {
      newErrors.service_address = "Please enter the service address.";
    }
    if (!formData.message.trim()) {
      newErrors.message = "Please describe the work needed";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Please provide more detail";
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    if (!formDef) return;
    const minSeconds = formDef?.spam?.min_seconds ?? 3;
    const elapsed = (Date.now() - fetchTime) / 1000;
    if (elapsed < minSeconds) {
      const waitMs = Math.ceil((minSeconds - elapsed) * 1000);
      await new Promise(resolve => setTimeout(resolve, waitMs));
    }
    setIsSubmitting(true);
    const values: Record<string, any> = {};
    const fullNameField = getField("full_name");
    if (fullNameField) values[fullNameField.id] = formData.full_name.trim();
    const phoneField = getField("phone");
    if (phoneField) values[phoneField.id] = formData.phone.trim();
    const emailField = getField("email");
    if (emailField) values[emailField.id] = formData.email.trim();
    const serviceTypeField = getField("service_type");
    if (serviceTypeField) values[serviceTypeField.id] = formData.service_type;
    const serviceAddressField = getField("service_address");
    if (serviceAddressField) values[serviceAddressField.id] = formData.service_address.trim();
    const messageField = getField("message");
    if (messageField) values[messageField.id] = formData.message.trim();
    const applianceTypeField = getField("appliance_type");
    if (applianceTypeField) values[applianceTypeField.id] = "";
    const preferredDateField = getField("preferred_date");
    if (preferredDateField && formData.preferred_date) {
      values[preferredDateField.id] = formData.preferred_date;
    }
    const sourcePageField = getField("source_page");
    if (sourcePageField) values[sourcePageField.id] = "Appliance Repair";
    const sourceCityField = getField("source_city");
    if (sourceCityField) values[sourceCityField.id] = "";
    const sourceServiceField = getField("source_service");
    if (sourceServiceField) values[sourceServiceField.id] = "Appliance Repair";
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setSubmitError("Submission service is unavailable.");
      setIsSubmitting(false);
      return;
    }
    const payload: any = {
      values,
      token: formDef.spam?.token,
      meta: {
        source_url: typeof window !== "undefined" ? window.location.href : "",
        referrer: typeof document !== "undefined" ? document.referrer : ""
      }
    };
    if (formDef.spam?.honeypot?.field) {
      payload.hp = {
        [formDef.spam.honeypot.field]: hpValue
      };
    }
    try {
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key || "homeowner_service_request"}/submit`, {
        method: "POST",
        data: payload
      });
      if (res?.success) {
        setIsSubmitted(true);
        if (res.confirmation?.type === "redirect" && res.confirmation.url) {
          if (typeof window !== "undefined") {
            window.location.assign(res.confirmation.url);
          }
        } else if (res.confirmation?.message) {
          setSuccessMessage(res.confirmation.message);
        }
      } else if (res?.code === "frms_validation_failed") {
        setServerErrors(res.errors || {});
        setSubmitError(res.message || "Please check the highlighted fields.");
      } else if (res?.code === "frms_invalid_token") {
        try {
          const fresh = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key || "homeowner_service_request"}`);
          setFormDef(fresh);
          setFetchTime(Date.now());
          setSubmitError("Your session expired. Please submit again.");
        } catch {
          setSubmitError("Submission session expired. Please refresh and try again.");
        }
      } else {
        setSubmitError(res?.message || "Submission failed. Please try again later.");
      }
    } catch (err: any) {
      setSubmitError(err?.message || "Submission failed. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const resetForm = () => {
    setIsSubmitted(false);
    setFormData({
      full_name: "",
      phone: "",
      email: "",
      service_type: "",
      service_address: "",
      message: "",
      preferred_date: ""
    });
    setErrors({});
    setServerErrors({});
    setSubmitError(null);
  };
  if (loadError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center text-[color:var(--muted-foreground)]">
        <AlertCircle className="size-8 mb-2 text-[color:var(--destructive)]" />
        <p className="text-sm font-medium">The service request form is currently unavailable. Please call us directly.</p>
      </div>;
  }
  if (!formDef) {
    return <div className="flex items-center justify-center p-12">
        <Loader className="size-6 animate-spin text-[color:var(--primary)]" />
      </div>;
  }
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-start gap-4 p-6 rounded-lg border border-[color:var(--border)] bg-[color:var(--background)]">
        <div className="text-base text-[color:var(--foreground)] font-default" dangerouslySetInnerHTML={{
        __html: successMessage
      }} />
        <Button onClick={resetForm} variant="outline">
          New request
        </Button>
      </div>;
  }
  const fullNameField = getField("full_name");
  const phoneField = getField("phone");
  const emailField = getField("email");
  const serviceTypeField = getField("service_type");
  const serviceAddressField = getField("service_address");
  const messageField = getField("message");
  const preferredDateField = getField("preferred_date");
  const serviceSelectOptions = serviceTypeField?.options && serviceTypeField.options.length > 0 ? serviceTypeField.options : serviceOptions.map(opt => ({
    value: opt,
    label: opt
  }));
  return <form onSubmit={handleSubmit} className="space-y-5">
      {formDef.spam?.honeypot?.field && <input type="text" name={formDef.spam.honeypot.field} value={hpValue} onChange={e => setHpValue(e.target.value)} tabIndex={-1} autoComplete="off" style={{
      position: "absolute",
      left: "-9999px"
    }} aria-hidden="true" />}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="grid gap-2">
          <label htmlFor="full_name" className="text-xs font-semibold uppercase tracking-[0.08em] text-[color:var(--muted-foreground)]">
            Full Name
          </label>
          <Input id="full_name" placeholder="Jane Smith" value={formData.full_name} onChange={e => {
          setFormData(prev => ({
            ...prev,
            full_name: e.target.value
          }));
          if (errors.full_name) setErrors(prev => ({
            ...prev,
            full_name: ""
          }));
        }} className="bg-[color:var(--background)] border-[color:var(--border)] rounded-md h-11 px-4 text-[color:var(--foreground)] focus-visible:border-[color:var(--ring)] focus-visible:ring-[color:var(--ring)]/20" />
          {getErrorMessage("full_name", fullNameField?.id) && <p className="text-destructive text-sm">
              {getErrorMessage("full_name", fullNameField?.id)}
            </p>}
        </div>

        <div className="grid gap-2">
          <label htmlFor="phone" className="text-xs font-semibold uppercase tracking-[0.08em] text-[color:var(--muted-foreground)]">
            Phone Number
          </label>
          <Input id="phone" type="tel" placeholder="(555) 000-0000" value={formData.phone} onChange={e => {
          setFormData(prev => ({
            ...prev,
            phone: e.target.value
          }));
          if (errors.phone) setErrors(prev => ({
            ...prev,
            phone: ""
          }));
        }} className="bg-[color:var(--background)] border-[color:var(--border)] rounded-md h-11 px-4 text-[color:var(--foreground)] focus-visible:border-[color:var(--ring)] focus-visible:ring-[color:var(--ring)]/20" />
          {getErrorMessage("phone", phoneField?.id) && <p className="text-destructive text-sm">
              {getErrorMessage("phone", phoneField?.id)}
            </p>}
        </div>
      </div>

      <div className="grid gap-2">
        <label htmlFor="email" className="text-xs font-semibold uppercase tracking-[0.08em] text-[color:var(--muted-foreground)]">
          Email Address
        </label>
        <Input id="email" type="email" placeholder="jane@example.com" value={formData.email} onChange={e => {
        setFormData(prev => ({
          ...prev,
          email: e.target.value
        }));
        if (errors.email) setErrors(prev => ({
          ...prev,
          email: ""
        }));
      }} className="bg-[color:var(--background)] border-[color:var(--border)] rounded-md h-11 px-4 text-[color:var(--foreground)] focus-visible:border-[color:var(--ring)] focus-visible:ring-[color:var(--ring)]/20" />
        {getErrorMessage("email", emailField?.id) && <p className="text-destructive text-sm">
            {getErrorMessage("email", emailField?.id)}
          </p>}
      </div>

      <div className="grid gap-2">
        <label className="text-xs font-semibold uppercase tracking-[0.08em] text-[color:var(--muted-foreground)]">
          Service Needed
        </label>
        <Select value={formData.service_type} onValueChange={val => {
        setFormData(prev => ({
          ...prev,
          service_type: val
        }));
        if (errors.service_type) setErrors(prev => ({
          ...prev,
          service_type: ""
        }));
      }}>
          <SelectTrigger className="w-full bg-[color:var(--background)] border-[color:var(--border)] rounded-md h-11 px-4 text-[color:var(--foreground)] focus-visible:border-[color:var(--ring)] focus-visible:ring-[color:var(--ring)]/20">
            <SelectValue placeholder="Select a service" />
          </SelectTrigger>
          <SelectContent>
            {serviceSelectOptions.map((option: any, i: number) => <SelectItem data-index={i} key={option.value || option} value={option.value || option}>
                {option.label || option}
              </SelectItem>)}
          </SelectContent>
        </Select>
        {getErrorMessage("service_type", serviceTypeField?.id) && <p className="text-destructive text-sm">
            {getErrorMessage("service_type", serviceTypeField?.id)}
          </p>}
      </div>

      <div className="grid gap-2">
        <label htmlFor="service_address" className="text-xs font-semibold uppercase tracking-[0.08em] text-[color:var(--muted-foreground)]">
          Service Address
        </label>
        <Input id="service_address" placeholder="Street address, city, state, ZIP" value={formData.service_address} onChange={e => {
        setFormData(prev => ({
          ...prev,
          service_address: e.target.value
        }));
        if (errors.service_address) setErrors(prev => ({
          ...prev,
          service_address: ""
        }));
      }} className="bg-[color:var(--background)] border-[color:var(--border)] rounded-md h-11 px-4 text-[color:var(--foreground)] focus-visible:border-[color:var(--ring)] focus-visible:ring-[color:var(--ring)]/20" />
        {getErrorMessage("service_address", serviceAddressField?.id) && <p className="text-destructive text-sm">
            {getErrorMessage("service_address", serviceAddressField?.id)}
          </p>}
      </div>

      <div className="grid gap-2">
        <label htmlFor="message" className="text-xs font-semibold uppercase tracking-[0.08em] text-[color:var(--muted-foreground)]">
          Describe the Problem
        </label>
        <Textarea id="message" rows={5} placeholder="Tell us what your AC, HVAC system, or appliance is doing..." value={formData.message} onChange={e => {
        setFormData(prev => ({
          ...prev,
          message: e.target.value
        }));
        if (errors.message) setErrors(prev => ({
          ...prev,
          message: ""
        }));
      }} className="bg-[color:var(--background)] border-[color:var(--border)] rounded-md px-4 py-3 text-[color:var(--foreground)] focus-visible:border-[color:var(--ring)] focus-visible:ring-[color:var(--ring)]/20" />
        {getErrorMessage("message", messageField?.id) && <p className="text-destructive text-sm">
            {getErrorMessage("message", messageField?.id)}
          </p>}
      </div>

      {preferredDateField && <div className="grid gap-2">
          <label htmlFor="preferred_date" className="text-xs font-semibold uppercase tracking-[0.08em] text-[color:var(--muted-foreground)]">
            Preferred Date
          </label>
          <Input id="preferred_date" type="date" value={formData.preferred_date} onChange={e => {
        setFormData(prev => ({
          ...prev,
          preferred_date: e.target.value
        }));
      }} className="bg-[color:var(--background)] border-[color:var(--border)] rounded-md h-11 px-4 text-[color:var(--foreground)] focus-visible:border-[color:var(--ring)] focus-visible:ring-[color:var(--ring)]/20" />
        </div>}

      {submitError && <div className="flex items-start gap-2 p-4 rounded-md border border-[color:var(--destructive)]/40 bg-[color:var(--destructive)]/10">
          <AlertCircle className="size-4 mt-0.5 text-[color:var(--destructive)]" />
          <p className="text-sm text-[color:var(--destructive)] font-default">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 bg-[color:var(--primary)] text-[color:var(--primary-foreground)] hover:bg-[color:var(--primary)]/90 rounded-md text-sm font-semibold uppercase tracking-[0.08em]">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Sending...
          </> : formDef?.submit?.label || "Submit Request"}
      </Button>

      <p className="text-xs text-[color:var(--muted-foreground)] text-center font-default">
        Our team reviews requests promptly during business hours.
      </p>
    </form>;
}
export default function Booking() {
  const shouldReduceMotion = useReducedMotion();
  return <section data-section-id="32735" id="bookingrequest" className="relative bg-[color:var(--background)] py-28 md:py-40 overflow-hidden">
      <div className="max-w-[1360px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 24
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-8">
              <span className="block w-[1.5px] h-[10px] bg-[color:var(--primary)]" aria-hidden="true" />
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[color:var(--primary)] font-default">
                Book a Pro
              </span>
              <span className="block w-[1.5px] h-[10px] bg-[color:var(--primary)]" aria-hidden="true" />
            </div>

            <h2 className="font-serif font-semibold text-[color:var(--foreground)] leading-[0.95] tracking-tight text-4xl md:text-5xl lg:text-6xl mb-8">
              Connect with local appliance repair pros.
            </h2>

            <p className="text-lg md:text-xl text-[color:var(--foreground)] leading-relaxed mb-10 font-default">
              Submit your request to get matched with independent contractors in the Atlanta metro area. Our platform helps you find verified trade pros who provide clear estimates for home repairs.
            </p>

            <div className="mb-10">
              <h2 className="text-sm font-semibold uppercase tracking-[0.15em] text-[color:var(--muted-foreground)] mb-5 font-default">
                What to prepare
              </h2>
              <ul className="space-y-3">
                {readyItems.map((item, i) => <li data-index={i} key={item} className="flex items-start gap-3 text-base text-[color:var(--foreground)] font-default">
                    <span className="mt-2 block size-1.5 bg-[color:var(--primary)] shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="pt-8 border-t border-[color:var(--border)] space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="size-4 mt-1 text-[color:var(--primary)] shrink-0" />
                <span className="text-sm text-[color:var(--muted-foreground)] font-medium tracking-wide font-default">
                  Serving the Metro Area
                </span>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="size-4 mt-1 text-[color:var(--primary)] shrink-0" />
                <Link to="/ContactPage" className="text-sm text-[color:var(--muted-foreground)] font-medium tracking-wide hover:text-[color:var(--primary)] transition-colors font-default">
                  handyheroesinc@gmail.com
                </Link>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="size-4 mt-1 text-[color:var(--primary)] shrink-0" />
                <Link to="tel:+14708479076" className="text-sm text-[color:var(--muted-foreground)] font-medium tracking-wide hover:text-[color:var(--primary)] transition-colors font-default">
                  (470) 847-9076
                </Link>
              </div>
            </div>
          </motion.div>

          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 24
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          ease: "easeOut",
          delay: 0.1
        }} className="lg:col-span-7">
            <Card className="bg-[color:var(--card)] border border-[color:var(--border)] rounded-xl p-6 md:p-10 shadow-md">
              <BookingForm />
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}